import { useCallback, useMemo } from 'react';
import { useListCompaniesForSelectionQuery } from '../../../../api/queries/listCompaniesForSelection';
import { useCompanies, useCompany } from '../../../../components/contexts/CompanyContextManager';
import useDownloadOptions from '../../../../components/fields/DownloadModal/useDownloadOptions';

export const allOption = { label: 'All Companies', value: 'all' };

const useGiftVoucherDownloadModalOptions = () => {
    const company = useCompany(true);
    const companiesContext = useCompanies(true);

    const { data, loading } = useListCompaniesForSelectionQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyId: company?.id,
            },
        },
    });

    const companies = useMemo(
        () =>
            data?.list?.items.length > 0
                ? data?.list?.items.map(company => ({
                      id: company.id,
                      displayName: company.displayName,
                      featurePermissions: {
                          hasGiftVoucherModules: company.availableModules.hasGiftVoucherModules,
                      },
                  }))
                : companiesContext.map(company => ({
                      id: company.id,
                      displayName: company.displayName,
                      featurePermissions: {
                          hasGiftVoucherModules: company.availableModules.hasGiftVoucherModules,
                      },
                  })),
        [companiesContext, data?.list?.items]
    );

    const companyOptions = useMemo(() => {
        const options = (companies || [])
            .filter(company => company.featurePermissions.hasGiftVoucherModules)
            .map(company => ({ label: company.displayName, value: company.id }));

        return [...(options.length > 1 ? [allOption] : []), ...options];
    }, [companies]);

    const getSelectedCompanyIds = useCallback(
        (selectedCompanyId: string) =>
            selectedCompanyId === allOption.value
                ? companyOptions.filter(option => option.value !== allOption.value).map(({ value }) => value)
                : [selectedCompanyId],
        [companyOptions]
    );

    const getSelectedCompanyName = useCallback(
        (selectedCompanyId: string) => {
            const filteredCompany = companyOptions.filter(option => option.value === selectedCompanyId);

            return filteredCompany.find(company => company.label === allOption.label) ? '' : filteredCompany[0].label;
        },
        [companyOptions]
    );

    const { timeframeOptions } = useDownloadOptions();

    return {
        loading,
        getSelectedCompanyIds,
        getSelectedCompanyName,
        companyOptions,
        timeframeOptions,
    };
};

export default useGiftVoucherDownloadModalOptions;
